import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useSidebarNav } from '../../context/SidebarNavContext';
import { useAuth } from '../../hooks/useAuth';
import { stages as stageService } from '../../services/apiService';
import LoadingSpinner from '../UI/LoadingSpinner';
import TalentAdaptiveShell from './TalentAdaptiveShell';
import {
  useNotificationCounts,
  useNotificationsNavigation,
} from './NotificationsWidget';

const Layout = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return savedTheme ? savedTheme === 'dark' : systemPrefersDark;
  });
  const [candidateStages, setCandidateStages] = useState([]);
  const {
    user,
    logout,
    loading,
    isAuthenticated,
    hasPermission,
    userPermissions,
  } = useAuth();
  const location = useLocation();
  const { sidebarNav, backPath } = useSidebarNav();

  const { unreadCount, unreadBySection: sectionCounts } = useNotificationCounts();
  useNotificationsNavigation();
  const unreadBySection = useMemo(
    () => ({ ...sectionCounts, '/notifications': unreadCount }),
    [sectionCounts, unreadCount],
  );

  useEffect(() => {
    if (!isAuthenticated || !hasPermission('talent-service', 'candidate', 'list')) return undefined;

    let cancelled = false;
    stageService.getStages({ talent_type: 'candidate' })
      .then((response) => {
        if (cancelled) return;
        const stages = Array.isArray(response?.data) ? response.data : [];
        setCandidateStages([...stages].sort(
          (left, right) => (left.stage_order || 0) - (right.stage_order || 0),
        ));
      })
      .catch((error) => console.error('Error loading candidate stages for nav:', error));

    return () => { cancelled = true; };
    // Keep the existing authentication-driven stage loading behavior. Permission
    // functions are available before their backing data finishes loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    document.documentElement.dataset.theme = isDarkMode ? 'dark' : 'light';
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-dark-bg-primary' : 'bg-light-bg-secondary'}`}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${isDarkMode ? 'from-gray-900 via-gray-800 to-gray-900' : 'from-primary-50 via-white to-primary-50'} flex items-center justify-center p-4`}>
        {children}
      </div>
    );
  }

  return (
    <TalentAdaptiveShell
      locationPathname={location.pathname}
      isDarkMode={isDarkMode}
      onThemeToggle={() => setIsDarkMode((current) => !current)}
      user={user}
      userPermissions={userPermissions}
      onLogout={logout}
      candidateStages={candidateStages}
      notificationCounts={unreadBySection}
      sidebarNav={sidebarNav}
      backPath={backPath}
    >
      {children}
    </TalentAdaptiveShell>
  );
};

export default Layout;
